import { useCallback } from 'react';
import { highlightedSessionIdsAtom, selectedFlowAtom, selectedSessionAtom } from '../../atom';
import { useSetAtom, useAtomValue, useAtom } from 'jotai';
import { FlowInstance } from '../../types';
import { SessionTimelineTableItem } from '@conviva/organism-components';
import { formatTimeDuration } from '../../utils/dataUtils';
import moment from 'moment';

export const useSelectedFlow = (flowsToSessions?: Record<string, Record<string, string[]>>) => {
  const [selectedFlow, setSelectedFlow] = useAtom(selectedFlowAtom);
  const setHighlightedSessionIds = useSetAtom(highlightedSessionIdsAtom);
  const setSelectedSession = useSetAtom(selectedSessionAtom);
  const selectedSession = useAtomValue(selectedSessionAtom);

  const handleSelectedFlowChange = useCallback(
    (flow: FlowInstance, sessionTableData: SessionTimelineTableItem[]) => {
      if (flow) {
        const { flow_identifier, flow_start_timestamp, flow_end_timestamp } = flow;

        if (!flow_identifier || !flow_start_timestamp || !flow_end_timestamp) {
          return;
        }

        const flowTimeRange = `${flow_start_timestamp}-${flow_end_timestamp}`;
        const sessionIds = flowsToSessions?.[flow_identifier]?.[flowTimeRange];
        if (!sessionIds) {
          return;
        }

        const sessions = sessionTableData.filter((session) =>
          sessionIds.includes(session.sessionId),
        );

        if (sessions.length === 0) return;
        if (sessions.length === 1) {
          setHighlightedSessionIds(sessionIds);
          setSelectedFlow(flow);
          setSelectedSession(sessions[0]);
          return;
        }
        const startTimestamps = sessions.map((session) => session.eventTimeStampStart);
        const endTimestamps = sessions.map((session) => session.eventTimeStampEnd);
        const earliestStart = startTimestamps.reduce((earliest, current) =>
          current < earliest ? current : earliest,
        );
        const latestEnd = endTimestamps.reduce((latest, current) =>
          current > latest ? current : latest,
        );
        const totalEventsSum = sessions.reduce((sum, session) => {
          const totalEvents =
            typeof session.totalEvents === 'number'
              ? session.totalEvents
              : parseInt(session.totalEvents as string) || 0;
          return sum + totalEvents;
        }, 0);
        const joinedSessionIds = sessionIds.join('/');

        const startMoment = moment(earliestStart);
        const endMoment = moment(latestEnd);
        const durationSeconds = endMoment.diff(startMoment, 'seconds');
        const userActiveTime = formatTimeDuration(durationSeconds);

        const calculatedSession: SessionTimelineTableItem = {
          ...sessions[0],
          sessionId: joinedSessionIds,
          eventTimeStampStart: earliestStart,
          eventTimeStampEnd: latestEnd,
          eventTimeStamp: `${earliestStart} - ${latestEnd}`,
          totalEvents: totalEventsSum,
          userActiveTime,
        };

        setSelectedSession(calculatedSession);
        setHighlightedSessionIds(sessionIds);
        setSelectedFlow(flow);
      }
    },
    [flowsToSessions, setHighlightedSessionIds, setSelectedFlow, setSelectedSession],
  );

  return {
    selectedFlow,
    handleSelectedFlowChange,
    selectedSession,
  };
};
